/**
 * FluxAccounts OAuth code flow with PKCE and continuity for the original SaID issuer. Only the
 * company and legacy account origins are accepted; historical client/storage identifiers remain
 * intentional protocol compatibility values.
 *
 * @module apps/web/src/lib/saidClient
 */

/** User projection returned by the selected issuer’s token exchange; application access is confirmed separately with the planner API. */
interface SaIdUser {
  /** Issuer account identifier returned by the token exchange. */
  id: string;
  /** Primary email reported by the identity issuer. */
  email: string;
  /** Optional human-readable name returned by the issuer. */
  display_name?: string | null;
  /** Issuer-reported role hint; application authorization remains server-enforced. */
  role?: 'admin' | 'user';
}

/** Issuer exchange envelope with an optional token/user payload or displayable error. Transport handling checks HTTP and envelope success before accepting data. */
interface SaIdTokenPayload {
  /** Whether the issuer reports a successful authorization-code exchange. */
  success?: boolean;
  /** Successful token response and associated identity projection. */
  data?: {
    /** Bearer credential exchanged for the planner session; never expose in UI or logs. */
    access_token: string;
    /** Issuer-reported access-token expiration timestamp. */
    expires_at: string;
    /** Granted OAuth scopes returned by the issuer. */
    scope: string;
    /** Identity projection associated with the returned token. */
    user: SaIdUser;
  };
  /** Issuer error envelope when code exchange fails. */
  error?: {
    /** Optional issuer explanation of a failed exchange. */
    message?: string;
  };
}

/** Optional Vite build-time configuration read once when this module loads. */
const env = (import.meta as { /** Optional Vite-injected build configuration, absent outside the bundled browser context. */ env?: Record<string, string | undefined> }).env;
/** Configured login issuer, defaulting to the company origin; checkedIssuer enforces the two-origin allowlist. */
const SAID_BASE_URL = env?.VITE_SAID_BASE_URL || 'https://accounts.fluxability.io';
/** Registered planner OAuth client ID, retaining the historical fluxiplanner default for protocol continuity. */
const SAID_CLIENT_ID = env?.VITE_SAID_CLIENT_ID || 'fluxiplanner';
/** Storage key binding a pending callback to the issuer selected at login initiation. */
const ISSUER_KEY = 'fluxability-oauth-issuer';
/** Approved original issuer used by historical pending login flows. */
const LEGACY_ISSUER = 'https://accounts.sahasta.com';
/** Approved company issuer; returned tokens are marked for company-session exchange. */
const COMPANY_ISSUER = 'https://accounts.fluxability.io';
/** Accept only the exact company or legacy issuer origin before sending PKCE credentials. Arbitrary configured or stored origins throw instead of receiving the verifier. */
function checkedIssuer(value: string): string {
  if (![LEGACY_ISSUER, COMPANY_ISSUER].includes(value)) throw new Error('Invalid identity issuer');
  return value;
}
/** Historical key for the pending PKCE secret in session and local storage. */
const VERIFIER_KEY = 'fluxiplanner-said-pkce-verifier';
/** Historical key for callback CSRF state and the associated return-path entry. */
const STATE_KEY = 'fluxiplanner-said-oauth-state';

/**
 * Build this planner origin's /auth/said/callback URL for both authorization and token exchange.
 */
export function saidRedirectUri(): string {
  return new URL('/auth/said/callback', window.location.origin).toString();
}

/**
 * Generate PKCE verifier/state, retain them in session and local storage with the checked issuer
 * and return path, and navigate to authorization for profile/email scopes.
 */
export async function startSaIdLogin(returnTo = '/teams'): Promise<void> {
  const issuer = checkedIssuer(SAID_BASE_URL);
  const verifier = randomVerifier();
  const state = crypto.randomUUID();
  sessionStorage.setItem(ISSUER_KEY, issuer);
  localStorage.setItem(ISSUER_KEY, issuer);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  localStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  localStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(`${STATE_KEY}:return`, returnTo);
  localStorage.setItem(`${STATE_KEY}:return`, returnTo);

  const authorize = new URL('/authorize', issuer);
  authorize.searchParams.set('client_id', SAID_CLIENT_ID);
  authorize.searchParams.set('redirect_uri', saidRedirectUri());
  authorize.searchParams.set('response_type', 'code');
  authorize.searchParams.set('state', state);
  authorize.searchParams.set('scope', 'profile email');
  authorize.searchParams.set('code_challenge_method', 'S256');
  authorize.searchParams.set('code_challenge', await codeChallenge(verifier));
  window.location.assign(authorize.toString());
}

/**
 * Validate callback state and PKCE verifier, exchange the code only at the checked issuer without redirects, then clear retained callback material. Company-issued tokens receive the fluxaccounts.v1 prefix; return paths are restricted to local paths. Returns null when no code is present.
 */
export async function completeSaIdLogin(): Promise<{ /** Exchanged bearer credential, prefixed for company-issued tokens before planner exchange. */ token: string; /** Identity projection returned by the checked issuer's token endpoint. */ user: SaIdUser; /** Validated local destination restored after the login callback. */ returnTo: string } | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) return null;

  const expectedState = sessionStorage.getItem(STATE_KEY) || localStorage.getItem(STATE_KEY);
  if (!expectedState || params.get('state') !== expectedState) {
    throw new Error('FluxAccounts sign-in state did not match. Please try again.');
  }

  const verifier = sessionStorage.getItem(VERIFIER_KEY) || localStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('FluxAccounts sign-in verifier was missing. Please try again.');

  const issuer = checkedIssuer(sessionStorage.getItem(ISSUER_KEY) || localStorage.getItem(ISSUER_KEY) || LEGACY_ISSUER);
  const response = await fetch(new URL('/api/oauth/token', issuer), {
    redirect: 'error',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      client_id: SAID_CLIENT_ID,
      redirect_uri: saidRedirectUri(),
      code,
      code_verifier: verifier,
    }),
  });
  const payload = await response.json().catch(() => null) as SaIdTokenPayload | null;
  if (!response.ok || !payload?.success || !payload.data) {
    throw new Error(payload?.error?.message || `FluxAccounts sign-in failed (${response.status})`);
  }

  const returnTo = sessionStorage.getItem(`${STATE_KEY}:return`) || localStorage.getItem(`${STATE_KEY}:return`) || '/teams';
  [ISSUER_KEY, VERIFIER_KEY, STATE_KEY, `${STATE_KEY}:return`].forEach(key => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });

  window.history.replaceState(null, '', '/auth/said/callback');
  return { token: issuer === COMPANY_ISSUER ? `fluxaccounts.v1.${payload.data.access_token}` : payload.data.access_token, user: payload.data.user, returnTo: returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.includes('\\') ? returnTo : '/teams' };
}

/** Hash the UTF-8 verifier with SHA-256 and encode the digest as unpadded base64url for S256 PKCE. Web Crypto failures reject the login initiation. */
async function codeChallenge(verifier: string): Promise<string> {
  return base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
}

/** Generate 32 cryptographically random bytes and encode them as an unpadded base64url PKCE verifier. */
function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

/** Encode bytes using the URL-safe base64 alphabet and remove padding; the result can be sent as an OAuth proof-key value. */
function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
